// /en/driving-license/$nationality — EN per-nationality detail
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { getDlNationality, DL_NATIONALITIES } from "@/data/driving-license-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, howToSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Car, ShieldCheck, Phone, MessageCircle, ArrowRight, Sparkles, Clock, Wallet, FileText, Languages } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/driving-license/$nationality")({
  loader: ({ params }) => {
    const n = getDlNationality(params.nationality);
    if (!n) throw notFound();
    return { slug: params.nationality };
  },
  head: ({ params }) => {
    const n = getDlNationality(params.nationality);
    if (!n) return { meta: [{ title: ctaTitle("Nationality not found", "/en/driving-license/$nationality", "en")}] };
    const url = `${SITE}/en/driving-license/${n.slug}`;
    const title = `Convert ${n.demonymEn} Driving License ${n.flag} → Thai 5-Year License · DLT ${n.dltDays}d | NYC`;
    const desc = `Convert your ${n.demonymEn} (${n.countryEn}) driving license to a Thai 5-year DLT license — ${n.directConversion ? "direct conversion, no road test": "written test required"} · ${n.needsTranslation ? "includes translation": "English license, no translation needed"} · DLT ${n.dltDays} days service. Send your documents for a free check.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/driving-license/$nationality", "en") }, { name: "description", content: ctaDesc(desc, "/en/driving-license/$nationality", "en")},
        { name: "keywords", content: n.keywordsEn.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/en/driving-license/$nationality", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/driving-license/$nationality", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/driving-license/${n.slug}`, `/en/driving-license/${n.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `Convert ${n.demonymEn} driving license to Thai license`,
              serviceType: "Driving License Conversion",
              description: desc,
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: { "@type": "Country", name: "Thailand" },
              audience: { "@type": "Audience", audienceType: n.demonymEn },
              offers: { "@type": "Offer", price: "4500", priceCurrency: "THB", description: "Translate + Embassy verify + DLT submission" },
            }),
          },
          howToSchema({
            name: `How to convert a ${n.demonymEn} driving license to a Thai license`,
            description: `Specific process for ${n.countryEn} driving licenses`,
            totalTime: `P${n.dltDays + 1}D`,
            estimatedCost: { currency: "THB", value: "4500" },
            inLanguage: "en",
            steps: [
              { name: "Prepare your original license", text: `${n.demonymEn} license (with at least 6 months validity remaining) + passport + visa/work permit + medical certificate` },
              ...(n.needsTranslation ? [{ name: "Translate the license", text: "MOJ-registered translator, THB 800-1,500", url: `${SITE}/en/translate` }] : []),
              ...(n.embassyVerification ? [{ name: "Embassy verification", text: `Visit the Embassy of ${n.countryEn} in Bangkok for affidavit/notarial verification` }] : []),
              { name: "Submit at DLT", text: `Department of Land Transport — vision/color/reaction tests${n.directConversion ? "" : " + 50-question written exam"} · ${n.dltDays} days` },
              { name: "Receive Thai 5-year license", text: "Smart card issued same day, THB 605" },
            ],
          }),
          faqPageSchema([
            { q: `Do ${n.demonymEn} drivers need to take a new road test?`, a: n.directConversion ? `No — ${n.countryEn} is a ${n.conventionStatus === "geneva_1949" ? "Geneva 1949" : n.conventionStatus === "vienna_1968" ? "Vienna 1968" : "bilateral reciprocity"} signatory, direct conversion applies.` : `A 50-question written test is required (50% pass mark), but no road test.` },
            { q: `Is a translation required?`, a: n.needsTranslation ? `Yes — must be translated by a MOJ-registered translator, THB 800-1,500.` : `No — ${n.countryEn} licenses are already in English.` },
            { q: `Where is the ${n.countryEn} embassy?`, a: n.noteEn },
            { q: `How long does it take?`, a: `DLT ${n.dltDays} days + translation/embassy 1-2 days = 2-5 working days total.` },
            { q: `Can I use my IDP from ${n.countryEn}?`, a: n.idpAccepted ? `IDP allows temporary 1-year driving, but for permanent residents we recommend the Thai 5-year license.` : `Not accepted — Thai license conversion is required.` },
          ]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "Driving License", item: `${SITE}/en/driving-license` },
                { "@type": "ListItem", position: 3, name: n.demonymEn, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: EnDlDetail,
});

function EnDlDetail() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const n = getDlNationality(slug)!;
  const others = DL_NATIONALITIES.filter((x) => x.slug !== slug && x.region === n.region).slice(0, 6);

  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-14 pb-10">
        <div className="mx-auto max-w-5xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/en" className="hover:text-primary">Home</Link><span className="mx-2">/</span>
            <Link to="/en/driving-license" className="hover:text-primary">Driving License</Link><span className="mx-2">/</span>
            <span>{n.demonymEn}</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Car className="mr-1 inline h-3 w-3" /> Driving License · {n.countryEn}
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            <span className="mr-3 text-4xl">{n.flag}</span>Convert {n.demonymEn} License → Thai 5-Year License
          </h1>
          <p className="mt-4 max-w-3xl text-base text-muted-foreground sm:text-lg">{n.leadEn}</p>

          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">How long does the {n.demonymEn} license conversion take?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              {n.demonymEn} drivers can convert to a Thai DLT 5-year license in {n.dltDays + 1}-{n.dltDays + 3} working days. {n.directConversion ? "No road test required" : "Written test (50 questions) required"} {n.needsTranslation ? "· translation included" : "· no translation needed (English)"} · NYC service THB 4,500 + DLT fee THB 605 · NYC has processed 8,000+ cases.
            </p>
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-4 max-w-3xl text-sm">
            <div className="rounded-lg border border-border bg-card p-2"><Clock className="h-4 w-4 text-primary" /><div className="text-[10px] text-muted-foreground">DLT</div><div className="font-bold">{n.dltDays}d</div></div>
            <div className="rounded-lg border border-border bg-card p-2"><FileText className="h-4 w-4 text-primary" /><div className="text-[10px] text-muted-foreground">Translation</div><div className="font-bold">{n.needsTranslation ? "Yes" : "No"}</div></div>
            <div className="rounded-lg border border-border bg-card p-2"><Car className="h-4 w-4 text-primary" /><div className="text-[10px] text-muted-foreground">Road test</div><div className="font-bold">{n.directConversion ? "No" : "Written"}</div></div>
            <div className="rounded-lg border border-border bg-card p-2"><Wallet className="h-4 w-4 text-primary" /><div className="text-[10px] text-muted-foreground">Service</div><div className="font-bold">THB 4.5k</div></div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> Call +66 92-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 space-y-12">
        <section>
          <h2 className="text-2xl font-extrabold"><Languages className="mr-2 inline h-6 w-6 text-primary" />Specific notes for {n.demonymEn} drivers</h2>
          <p className="mt-4 rounded-2xl border-l-4 border-primary bg-primary/5 p-4 text-sm leading-relaxed">{n.noteEn}</p>
        </section>

        <section>
          <h2 className="text-2xl font-extrabold">Key Facts</h2>
          <table className="mt-4 w-full text-sm">
            <tbody className="divide-y divide-border">
              {[
                ["Nationality", `${n.flag} ${n.countryEn} (${n.demonymEn})`],
                ["Convention", n.conventionStatus === "geneva_1949" ? "Geneva 1949" : n.conventionStatus === "vienna_1968" ? "Vienna 1968" : n.conventionStatus === "both" ? "Geneva + Vienna" : "Bilateral"],
                ["Direct conversion", n.directConversion ? "✅ Yes — no road test" : "📝 Written test required"],
                ["Translation required", n.needsTranslation ? "Yes (THB 800-1,500)" : "No (English license)"],
                ["Embassy verification", n.embassyVerification ? "Yes" : "Not required"],
                ["DLT processing", `${n.dltDays} working days`],
                ["DLT fee", "THB 605 (5-year smart card)"],
                ["NYC service fee", "THB 4,500 (full handling)"],
                ["IDP accepted", n.idpAccepted ? "✅ Temporary 1-year use" : "❌ Not accepted"],
              ].map(([k, v]) => (
                <tr key={k}><td className="py-2 pr-4 font-semibold">{k}</td><td className="py-2">{v}</td></tr>
              ))}
            </tbody>
          </table>
        </section>

        <section>
          <h2 className="text-2xl font-extrabold"><ShieldCheck className="mr-2 inline h-6 w-6 text-primary" />Related services</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Link to="/en/translate" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
              <div className="font-bold">Document translation</div>
              <div className="mt-1 text-xs text-muted-foreground">ISO 17100 · MOJ-registered translators</div>
            </Link>
            <Link to="/en/work-permit/$nationality" params={{ nationality: n.slug === "emirati" ? "japanese" : n.slug }} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
              <div className="font-bold">Work Permit + Non-B Visa</div>
              <div className="mt-1 text-xs text-muted-foreground">For {n.demonymEn} professionals</div>
            </Link>
            <Link to="/en/notary" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
              <div className="font-bold">Notary Public Attorney</div>
              <div className="mt-1 text-xs text-muted-foreground">Affidavit, POA, certifications</div>
            </Link>
            <Link to="/en/apostille" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
              <div className="font-bold">Apostille (Hague)</div>
              <div className="mt-1 text-xs text-muted-foreground">For documents going abroad</div>
            </Link>
          </div>
        </section>

        {others.length > 0 && (
          <section className="border-t border-border pt-10">
            <h2 className="text-2xl font-extrabold">Other nationalities in the same region</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {others.map((o) => (
                <Link key={o.slug} to="/en/driving-license/$nationality" params={{ nationality: o.slug }} className="group rounded-2xl border border-border bg-card p-4 hover:border-primary">
                  <div className="flex items-center gap-2">
                    <div className="text-2xl">{o.flag}</div>
                    <div className="font-bold">{o.countryEn}</div>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">DLT {o.dltDays}d · {o.directConversion ? "Direct" : "Written test"}</div>
                  <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary">View details <ArrowRight className="h-3 w-3" /></div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
